import { createContext, Dispatch, ReactNode, useContext, useMemo, useReducer } from "react";
import insightSlice, { initialState, InsightStateProps } from "./insightSlice";
import { UnknownAction } from "@reduxjs/toolkit";
import { StreamingContextProvider } from "@/modules/streaming";

interface ContextProps {
  state: InsightStateProps;
  dispatch: Dispatch<UnknownAction>;
}

export const InsightContext = createContext<ContextProps>({
  state: initialState,
  dispatch: () => undefined,
});

interface InsightContextProviderProps {
  children: ReactNode;
}

const InsightContextProvider = ({ children }: InsightContextProviderProps): JSX.Element => {
  const [state, dispatch] = useReducer(insightSlice.reducer, initialState);

  const values = useMemo(
    () => ({
      state,
      dispatch,
    }),
    [state, dispatch]
  );

  return (
    <InsightContext.Provider value={values}>
      <StreamingContextProvider>{children}</StreamingContextProvider>
    </InsightContext.Provider>
  );
};

export const useInsightState = () => {
  const context = useContext(InsightContext);
  if (context === undefined) {
    throw new Error("useInsightState must be used within an InsightContextProvider");
  }
  return context.state;
};

export default InsightContextProvider;
